/**
 * The `/` hotkey (INVENTORY-DESKTOP §2.4; prototype `_onK`, lines 1186-1192).
 *
 * Pressing `/` anywhere focuses the Discover search input, unless it already
 * has focus. The prototype gated on `session && screen ∈ {discover, tonight}`
 * and reached for `searchRef` or `tSearchRef` — but `tSearchRef` is never bound
 * to any element, so the Tonight branch is dead (§9.5). This ports the live
 * half: whichever screen has REGISTERED an input gets the focus, which is
 * Discover today and is automatically correct if a Tonight search ever ships.
 *
 * Two pieces, deliberately split so there is no import cycle between the shell
 * and the screens:
 *   `useSearchHotkey()`     — install the window listener. `DesktopApp` calls
 *                             it once; nobody else should.
 *   `useSearchInputRef()`   — a screen calls this and spreads the returned ref
 *                             onto its `<input>`. Registration is automatic and
 *                             is torn down when the screen unmounts.
 *
 * A guard the prototype also has: if the input is already `document.activeElement`
 * the key is NOT swallowed, so typing `/` inside the search box types a slash.
 */

import { useCallback, useEffect, useRef } from 'react';

let target: HTMLInputElement | null = null;

/**
 * Screens: `<input ref={useSearchInputRef()} … />`
 *
 * Registration lives ENTIRELY in the ref callback — attach registers, detach
 * (React calls the ref with `null`) unregisters. There is deliberately no
 * effect here.
 *
 * WHY (the bug this shape fixes, found by D2): the first cut registered from
 * the ref callback but unregistered from a passive-effect cleanup. React 18.3
 * StrictMode's dev double-invoke runs passive create → destroy → create WITHOUT
 * detaching and re-attaching refs, so at destroy time `target === mine.current`
 * held, `target` became `null`, and the ref was never called again — the `/`
 * hotkey was dead for the whole dev session. Production (no StrictMode) was
 * unaffected, which is exactly the kind of asymmetry not to ship.
 *
 * Ref attach/detach is symmetric under every React 18 and 19 StrictMode
 * schedule: attach → detach → attach ends registered, and a lone detach ends
 * unregistered. The `mine.current` guard keeps a screen from clearing another
 * screen's registration if two search inputs are ever mounted at once (only
 * Discover has one today) — last attached wins, and only the owner may clear.
 *
 * The callback identity is stable (`useCallback` with no deps), so React never
 * detaches and re-attaches it across re-renders.
 */
export function useSearchInputRef(): (el: HTMLInputElement | null) => void {
  const mine = useRef<HTMLInputElement | null>(null);

  return useCallback((el: HTMLInputElement | null) => {
    if (el) {
      mine.current = el;
      target = el;
      return;
    }
    // detach — only clear the registry if we are still the registered input
    if (target === mine.current) target = null;
    mine.current = null;
  }, []);
}

/** Shell: install once. */
export function useSearchHotkey(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== '/') return;
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const el = target;
      if (!el || document.activeElement === el) return;
      ev.preventDefault();
      el.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}

/** Test seam. */
export function resetSearchHotkey(): void {
  target = null;
}
